<template>
  <div class="projects-page">
    <!-- Hero Section -->
    <div class="projects-hero">
      <div class="container">
        <div class="hero-terminal">
          <div class="terminal-line">
            <span class="prompt">$</span> ls ~/projects/
          </div>
          <h1 class="hero-title">projects<span class="cursor">_</span></h1>
          <p class="hero-subtitle">A collection of things I've built and contributed to</p>
        </div>
      </div>
      <div class="hero-bg"></div>
    </div>

    <div class="container py-5">
      <!-- Projects Grid -->
      <section class="projects-section">
        <h2 class="section-title">
          <span class="title-prefix">01.</span> featured_projects
        </h2>

        <!-- PaceCtrl Project -->
        <div class="project-card featured">
          <div class="project-header">
            <div class="project-meta">
              <span class="project-tag" data-bloop-id="tag_showroom" data-bloop-kind="tag">ICT Showroom 2026</span>
              <span class="project-tag collab" data-bloop-id="tag_collab" data-bloop-kind="tag">Abo Akademi x Demola</span>
            </div>
            <a href="https://pacectrl.up.railway.app/" target="_blank" class="project-link">
              <span class="link-icon">[~]</span>
            </a>
          </div>

          <div class="project-content">
            <div class="row">
              <div class="col-lg-7">
                <h3 class="project-title" data-bloop-id="project_pacectrl" data-bloop-kind="project">PaceCtrl</h3>
                <p class="project-tagline">Pioneering Sustainable Maritime Solutions</p>

                <div class="terminal-card project-description">
                  <div class="terminal-card-header">
                    <div class="terminal-dots">
                      <span class="terminal-dot red"></span>
                      <span class="terminal-dot yellow"></span>
                      <span class="terminal-dot green"></span>
                    </div>
                    <span class="terminal-card-title">~/pacectrl/README.md</span>
                  </div>
                  <div class="terminal-card-body">
                    <p>PaceCtrl is a student-led project from Abo Akademi, developed in collaboration with Demola. We help ferry operators offer greener, slower voyages by surfacing speed-and-emissions trade-offs directly to passengers during booking &mdash; at no extra cost to either side.</p>

                    <div class="highlight-box">
                      <h4>Why Speed Matters</h4>
                      <p>Ship propulsion follows a cubic law &mdash; reducing speed by just 10% cuts fuel consumption by roughly 27%. Yet passengers are rarely given the choice. A survey conducted as part of this project confirmed that many travelers are willing to arrive slightly later if it means meaningfully fewer emissions.</p>
                    </div>
                  </div>
                </div>

                <div class="stats-grid">
                  <div class="stat-item" data-bloop-id="stat_fuel10" data-bloop-kind="stat">
                    <span class="stat-value green">~27%</span>
                    <span class="stat-label">less fuel at -10% speed</span>
                  </div>
                  <div class="stat-item" data-bloop-id="stat_fuel20" data-bloop-kind="stat">
                    <span class="stat-value cyan">~50%</span>
                    <span class="stat-label">less fuel at -20% speed</span>
                  </div>
                  <div class="stat-item" data-bloop-id="stat_cost" data-bloop-kind="stat">
                    <span class="stat-value amber">0 EUR</span>
                    <span class="stat-label">extra cost for passengers</span>
                  </div>
                </div>
              </div>

              <div class="col-lg-5">
                <div class="terminal-card how-it-works">
                  <div class="terminal-card-header">
                    <div class="terminal-dots">
                      <span class="terminal-dot red"></span>
                      <span class="terminal-dot yellow"></span>
                      <span class="terminal-dot green"></span>
                    </div>
                    <span class="terminal-card-title">system_flow.sh</span>
                  </div>
                  <div class="terminal-card-body">
                    <div class="flow-step" data-bloop-id="step_widget" data-bloop-kind="step">
                      <span class="step-number">01</span>
                      <div class="step-content">
                        <h5>Widget Embedded</h5>
                        <p>Ferry operators drop a single script tag into their checkout page. The widget fetches voyage-specific configuration from the PaceCtrl API.</p>
                      </div>
                    </div>
                    <div class="flow-step" data-bloop-id="step_speed" data-bloop-kind="step">
                      <span class="step-number">02</span>
                      <div class="step-content">
                        <h5>Passenger Chooses Speed</h5>
                        <p>Passengers drag an interactive slider to trade travel time for fewer emissions. Real-time feedback shows estimated CO2 savings.</p>
                      </div>
                    </div>
                    <div class="flow-step" data-bloop-id="step_booking" data-bloop-kind="step">
                      <span class="step-number">03</span>
                      <div class="step-content">
                        <h5>Booking Confirmed</h5>
                        <p>After payment, the operator backend posts a webhook to PaceCtrl. Aggregate data flows into the operator analytics dashboard.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="my-role">
                  <div class="role-header">
                    <span class="role-icon">&gt;</span>
                    my_contribution
                  </div>
                  <p>Backend development - Building APIs, webhooks, and data infrastructure.</p>
                </div>
              </div>
            </div>

            <div class="project-footer">
              <div class="tech-used">
                <span class="tech-label">// tech_stack:</span>
                <span class="tech-item" data-bloop-id="chip_apis" data-bloop-kind="chip">Backend APIs</span>
                <span class="tech-item" data-bloop-id="chip_webhooks" data-bloop-kind="chip">Webhooks</span>
                <span class="tech-item" data-bloop-id="chip_analytics" data-bloop-kind="chip">Analytics</span>
              </div>
              <a href="https://pacectrl.up.railway.app/" target="_blank" class="project-cta">
                View Live Demo <span class="arrow">&rarr;</span>
              </a>
            </div>
          </div>
        </div>

        <div class="project-card" data-bloop-signal data-bloop-confetti>
          <div class="project-header">
            <div class="project-meta">
              <span class="project-tag">personal</span>
            </div>
          </div>
          <div class="project-content">
            <h3 class="project-title" data-bloop-id="project_bloop" data-bloop-kind="project" data-bloop-label="Bloop">Bloop</h3>
            <p class="project-tagline">A second cursor</p>
            <p class="bloop-blurb" data-bloop-id="project_bloop_line" data-bloop-kind="line" data-bloop-label="Jev, a TypeSafe System One model">You may notice another cursor on these pages. Jev, a <a href="https://www.typesafe.ai/" target="_blank" rel="noopener noreferrer">TypeSafe</a> System One model, decides what it does.</p>
          </div>
        </div>

      </section>


      <!-- More Projects Coming -->
      <section class="coming-soon-section">
        <div class="terminal-card">
          <div class="terminal-card-header">
            <div class="terminal-dots">
              <span class="terminal-dot red"></span>
              <span class="terminal-dot yellow"></span>
              <span class="terminal-dot green"></span>
            </div>
            <span class="terminal-card-title">$ tail -f projects.log</span>
          </div>
          <div class="terminal-card-body text-center">
            <div class="loading-animation">
              <span class="loading-bracket">[</span>
              <span class="loading-bar"></span>
              <span class="loading-bracket">]</span>
            </div>
            <p class="coming-text">
              <span class="comment"># More projects loading...</span>
            </p>
            <p class="coming-subtext">This list will be updated with more projects soon.</p>
            <p class="coming-subtext dim">Check back later or follow my GitHub for updates. <br>I have a couple of personal side-projects in the waiting list, that I will start working on as time allows</p>
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectsView',
}
</script>

<style scoped>
/* Hero Section */
.projects-hero {
  min-height: 40vh;
  display: flex;
  align-items: center;
  position: relative;
  padding-top: 100px;
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background:
    radial-gradient(ellipse at 30% 70%, rgba(57, 255, 20, 0.08) 0%, transparent 50%),
    radial-gradient(ellipse at 70% 30%, rgba(0, 217, 255, 0.06) 0%, transparent 50%),
    var(--terminal-bg);
  z-index: -1;
}

.hero-terminal {
  font-family: 'JetBrains Mono', monospace;
}

.terminal-line {
  color: var(--terminal-text-dim);
  margin-bottom: 15px;
  font-size: 1rem;
}

.prompt {
  color: var(--terminal-green);
  margin-right: 8px;
}

.hero-title {
  font-size: 3.5rem;
  color: var(--terminal-text);
  margin-bottom: 15px;
}

.cursor {
  color: var(--terminal-green);
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

.hero-subtitle {
  color: var(--terminal-text-dim);
  font-size: 1.1rem;
}


/* Section Styling */
.section-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.8rem;
  color: var(--terminal-text);
  margin-bottom: 40px;
}

.title-prefix {
  color: var(--terminal-green);
  margin-right: 10px;
}


/* Project Card */
.project-card {
  background: var(--terminal-card-bg);
  border: 1px solid var(--terminal-border);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 40px;
  transition: all 0.3s ease;
}

.project-card:hover {
  border-color: var(--terminal-green);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3), 0 0 30px rgba(57, 255, 20, 0.1);
}

.project-card.featured {
  border-left: 3px solid var(--terminal-green);
}

.project-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  background: var(--terminal-bg);
  border-bottom: 1px solid var(--terminal-border);
}

.project-meta {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.project-tag {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.75rem;
  padding: 4px 12px;
  background: var(--terminal-card-bg);
  border: 1px solid var(--terminal-border);
  border-radius: 4px;
  color: var(--terminal-text-dim);
}

.project-tag.collab {
  border-color: var(--terminal-cyan);
  color: var(--terminal-cyan);
}

.project-tag.bloop-hover,
.stat-item.bloop-hover,
.flow-step.bloop-hover,
.tech-used .tech-item.bloop-hover,
.project-title.bloop-hover {
  border-color: var(--terminal-green);
  box-shadow: 0 0 12px rgba(57, 255, 20, 0.25);
}

.project-link {
  font-family: 'JetBrains Mono', monospace;
  color: var(--terminal-green);
  font-size: 1.2rem;
  transition: all 0.3s ease;
}

.project-link:hover {
  color: var(--terminal-cyan);
  text-shadow: 0 0 10px var(--terminal-cyan);
}

.project-content {
  padding: 30px;
}

.project-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 2rem;
  color: var(--terminal-green);
  margin-bottom: 8px;
}

.project-tagline {
  color: var(--terminal-cyan);
  font-size: 1.1rem;
  margin-bottom: 25px;
  font-style: italic;
}

.bloop-blurb {
  max-width: 38rem;
  margin: 0;
  color: var(--terminal-text);
  font-family: 'JetBrains Mono', monospace;
  line-height: 1.6;
}

.bloop-blurb a {
  color: var(--terminal-cyan);
  text-underline-offset: 3px;
}

.bloop-blurb a:hover,
.bloop-blurb a:focus-visible {
  color: var(--terminal-green);
}


/* Terminal Card */
.terminal-card {
  background: var(--terminal-card-bg);
  border: 1px solid var(--terminal-border);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 25px;
}

.terminal-card-header {
  background: var(--terminal-bg);
  padding: 10px 15px;
  border-bottom: 1px solid var(--terminal-border);
  display: flex;
  align-items: center;
  gap: 8px;
}

.terminal-dots {
  display: flex;
  gap: 6px;
}

.terminal-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.terminal-dot.red { background: #ff5f56; }
.terminal-dot.yellow { background: #ffbd2e; }
.terminal-dot.green { background: #27c93f; }

.terminal-card-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  color: var(--terminal-text-dim);
  margin-left: 10px;
}

.terminal-card-body {
  padding: 20px;
}

.terminal-card-body p {
  color: var(--terminal-text);
  line-height: 1.7;
  margin-bottom: 15px;
}

.highlight-box {
  background: var(--terminal-bg);
  border-left: 3px solid var(--terminal-amber);
  padding: 15px 20px;
  margin-top: 20px;
  border-radius: 0 8px 8px 0;
}

.highlight-box h4 {
  font-family: 'JetBrains Mono', monospace;
  color: var(--terminal-amber);
  font-size: 1rem;
  margin-bottom: 10px;
}

.highlight-box p {
  margin-bottom: 0;
  font-size: 0.95rem;
  color: var(--terminal-text-dim);
}


/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 30px 0;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: var(--terminal-bg);
  border: 1px solid var(--terminal-border);
  border-radius: 8px;
}

.stat-value {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 5px;
}

.stat-value.green { color: var(--terminal-green); }
.stat-value.cyan { color: var(--terminal-cyan); }
.stat-value.amber { color: var(--terminal-amber); }

.stat-label {
  font-size: 0.8rem;
  color: var(--terminal-text-dim);
}


/* How It Works Flow */
.flow-step {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--terminal-border);
}

.flow-step:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.step-number {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
  color: var(--terminal-green);
  flex-shrink: 0;
}

.step-content h5 {
  font-family: 'JetBrains Mono', monospace;
  color: var(--terminal-cyan);
  font-size: 0.95rem;
  margin-bottom: 8px;
}

.step-content p {
  font-size: 0.85rem;
  color: var(--terminal-text-dim);
  margin-bottom: 0;
}


/* My Role */
.my-role {
  background: var(--terminal-bg);
  border: 1px solid var(--terminal-green);
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

.role-header {
  font-family: 'JetBrains Mono', monospace;
  color: var(--terminal-green);
  font-size: 1rem;
  margin-bottom: 10px;
}

.role-icon {
  margin-right: 8px;
}

.my-role p {
  color: var(--terminal-text);
  margin-bottom: 0;
  font-size: 0.95rem;
}


/* Project Footer */
.project-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 25px;
  border-top: 1px solid var(--terminal-border);
  flex-wrap: wrap;
  gap: 20px;
}

.tech-used {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tech-label {
  font-family: 'JetBrains Mono', monospace;
  color: var(--terminal-text-dim);
  font-size: 0.8rem;
}

.tech-used .tech-item {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  padding: 4px 10px;
  background: var(--terminal-bg);
  border: 1px solid var(--terminal-border);
  border-radius: 4px;
  color: var(--terminal-text);
}

.project-cta {
  font-family: 'JetBrains Mono', monospace;
  color: var(--terminal-green) !important;
  border: 1px solid var(--terminal-green);
  padding: 10px 20px;
  border-radius: 6px;
  transition: all 0.3s ease;
  text-decoration: none !important;
}

.project-cta:hover {
  background: var(--terminal-green);
  color: var(--terminal-bg) !important;
  box-shadow: 0 0 20px rgba(57, 255, 20, 0.3);
}

.project-cta .arrow {
  margin-left: 8px;
  display: inline-block;
  transition: transform 0.3s ease;
}

.project-cta:hover .arrow {
  transform: translateX(5px);
}


/* Coming Soon Section */
.coming-soon-section {
  padding: 60px 0;
}

.loading-animation {
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.5rem;
  color: var(--terminal-text-dim);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.loading-bracket {
  color: var(--terminal-green);
}

.loading-bar {
  width: 200px;
  height: 8px;
  background: var(--terminal-bg);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}

.loading-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: -50%;
  width: 50%;
  height: 100%;
  background: linear-gradient(90deg, transparent, var(--terminal-green), transparent);
  animation: loading 1.5s ease-in-out infinite;
}

@keyframes loading {
  0% { left: -50%; }
  100% { left: 100%; }
}

.coming-text {
  font-family: 'JetBrains Mono', monospace;
  margin-bottom: 15px;
}

.comment {
  color: var(--terminal-green);
}

.coming-subtext {
  color: var(--terminal-text);
  margin-bottom: 10px;
}

.coming-subtext.dim {
  color: var(--terminal-text-dim);
  font-size: 0.9rem;
}


/* Responsive */
@media (max-width: 992px) {
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .hero-title {
    font-size: 2.5rem;
  }
}

@media (max-width: 768px) {
  .project-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .project-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-title {
    font-size: 2rem;
  }

  .section-title {
    font-size: 1.4rem;
  }
}
</style>
